import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, FlexGap, Paragraph } from '@treams/treams-ui';

import { RecordIcon } from '../../../Recording/RecordConversation';
import { TranscriptionModule } from '../../../Recording/TranscriptionModule';
import { getEvaluationSubmissionRecordingAvailability } from './evaluationSubmissionRecordingAvailability';

type Props = {
	submission: graphql.EvaluationSubmissionFragment;
	onRefresh: () => void;
};

export default function EvaluationSubmissionTranscription({ submission, onRefresh }: Props) {
	const { t } = useTranslation();
	const soundRecorderV2 = useFeature(graphql.FeatureName.SoundRecorderV2);

	const [linkTranscription] = graphql.useLinkTranscriptionToEvaluationSubmissionMutation();
	const [unlinkTranscription, { loading: unlinkLoading }] =
		graphql.useUnlinkTranscriptionFromEvaluationSubmissionMutation();

	const onUnlinkTranscription = useCallback(
		async (transcriptionIDFromCaller?: string) => {
			const transcriptionID = transcriptionIDFromCaller ?? submission.transcription?.id;
			if (!transcriptionID) return;
			await unlinkTranscription({
				variables: {
					input: {
						evaluationSubmissionID: submission.id,
						transcriptionID,
					},
				},
			});
		},
		[submission.id, submission.transcription?.id, unlinkTranscription],
	);

	const viewerCanRecord = submission.manager.viewerCanUpdate;
	const recordingAvailability = getEvaluationSubmissionRecordingAvailability(submission);

	if (recordingAvailability === 'onlyManagerCanRecord') {
		return (
			<FlexGap mb="16px" alignItems="center" gap={8}>
				<Button
					title={t('transcription.recordConversation')}
					type="secondary"
					small
					disabled
					icon={<RecordIcon style={{ opacity: 0.5 }} />}
				/>
				<Paragraph small>{t('transcription.onlyManagerCanRecord')}</Paragraph>
			</FlexGap>
		);
	}

	if (recordingAvailability === 'waitUntilShared') {
		return (
			<FlexGap mb="16px" alignItems="center" gap={8}>
				<Button
					title={t('transcription.recordConversation')}
					type="secondary"
					small
					disabled
					icon={<RecordIcon style={{ opacity: 0.5 }} />}
				/>
				<Paragraph small>
					{t('transcription.notAvailableUntilShared', {
						name: submission.employee.user.firstName,
					})}
				</Paragraph>
			</FlexGap>
		);
	}

	return (
		<TranscriptionModule
			targetID={submission.id}
			targetType={graphql.TranscriptionTargetType.EvaluationSubmission}
			transcription={submission.transcription}
			onLinkTranscription={async (transcriptionID) => {
				await linkTranscription({
					variables: {
						input: {
							evaluationSubmissionID: submission.id,
							transcriptionID,
						},
					},
				});
			}}
			onUnlinkTranscription={onUnlinkTranscription}
			unlinkLoading={unlinkLoading}
			onRefresh={onRefresh}
			canRemove={viewerCanRecord}
			viewerCanRecord={viewerCanRecord}
			disableRecording={!submission.employee.hasShared || !submission.manager.hasShared}
			streamingEnabled={soundRecorderV2}
		/>
	);
}
